import React, { useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader, Section, EmptyState } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { PROTEIN_CATEGORIES } from "@/lib/menu-utils";
import { useAuth } from "@/context/AuthContext";

export default function Proteins() {
  const { can } = useAuth();
  const canWriteProt = can("proteins:write");
  const canWritePrep = can("preparations:write");
  const [proteins, setProteins] = useState([]);
  const [preparations, setPreparations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Protein modal
  const [pOpen, setPOpen] = useState(false);
  const [pEditId, setPEditId] = useState(null);
  const [pForm, setPForm] = useState({ name: "", category: "outros", notes: "" });

  // Preparation modal
  const [prepOpen, setPrepOpen] = useState(false);
  const [prepEditId, setPrepEditId] = useState(null);
  const [prepForm, setPrepForm] = useState({ protein_id: "", name: "", grams_per_meal: 120, notes: "" });

  // Delete confirmations
  const [deleteProteinId, setDeleteProteinId] = useState(null);
  const [deletePrepId, setDeletePrepId] = useState(null);

  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: pr }] = await Promise.all([api.get("/proteins"), api.get("/preparations")]);
      setProteins(p);
      setPreparations(pr);
      if (Object.keys(expanded).length === 0 && p.length) {
        setExpanded(Object.fromEntries(p.map(x => [x.id, true])));
      }
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const prepsByProtein = useMemo(() => {
    const m = {};
    for (const pr of preparations) { (m[pr.protein_id] = m[pr.protein_id] || []).push(pr); }
    return m;
  }, [preparations]);

  const openCreateProtein = () => { setPEditId(null); setPForm({ name: "", category: "outros", notes: "" }); setPOpen(true); };
  const openEditProtein = (p) => { setPEditId(p.id); setPForm({ name: p.name, category: p.category, notes: p.notes || "" }); setPOpen(true); };

  const submitProtein = async (e) => {
    e.preventDefault();
    try {
      if (pEditId) await api.put(`/proteins/${pEditId}`, pForm);
      else await api.post("/proteins", pForm);
      toast.success(pEditId ? "Proteína atualizada" : "Proteína cadastrada");
      setPOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const openCreatePrep = (protein_id = "") => {
    setPrepEditId(null);
    setPrepForm({ protein_id: protein_id || (proteins[0]?.id || ""), name: "", grams_per_meal: 120, notes: "" });
    setPrepOpen(true);
  };
  const openEditPrep = (pr) => {
    setPrepEditId(pr.id);
    setPrepForm({ protein_id: pr.protein_id, name: pr.name, grams_per_meal: pr.grams_per_meal, notes: pr.notes || "" });
    setPrepOpen(true);
  };

  const submitPrep = async (e) => {
    e.preventDefault();
    if (!prepForm.protein_id) { toast.error("Selecione uma proteína"); return; }
    try {
      const payload = { ...prepForm, grams_per_meal: Number(prepForm.grams_per_meal) };
      if (prepEditId) await api.put(`/preparations/${prepEditId}`, payload);
      else await api.post("/preparations", payload);
      toast.success(prepEditId ? "Preparação atualizada" : "Preparação cadastrada");
      setPrepOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const doDeleteProtein = async () => {
    try { await api.delete(`/proteins/${deleteProteinId}`); toast.success("Proteína removida"); setDeleteProteinId(null); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const doDeletePrep = async () => {
    try { await api.delete(`/preparations/${deletePrepId}`); toast.success("Preparação removida"); setDeletePrepId(null); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div data-testid="proteins-page">
      <PageHeader
        title="Proteínas & Preparações"
        subtitle="Cadastre as proteínas e suas preparações com gramagem por refeição."
        actions={
          <div className="flex gap-2">
            {canWritePrep && (
              <Button variant="outline" onClick={() => openCreatePrep()} disabled={proteins.length === 0} data-testid="add-preparation-btn">
                <Plus size={16} className="mr-1" /> Preparação
              </Button>
            )}
            {canWriteProt && (
              <Button onClick={openCreateProtein} data-testid="add-protein-btn" className="bg-emerald-900 hover:bg-emerald-800 text-white">
                <Plus size={16} className="mr-1" /> Proteína
              </Button>
            )}
          </div>
        }
      />

      <Section>
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Carregando…</div>
        ) : proteins.length === 0 ? (
          <EmptyState
            title="Nenhuma proteína cadastrada"
            description="Cadastre proteínas (Frango, Bovina, Suína, Peixe, Ovo…) e depois adicione as preparações com gramagem por refeição."
            action={canWriteProt ? <Button onClick={openCreateProtein} className="bg-emerald-900 hover:bg-emerald-800 text-white"><Plus size={16} className="mr-1"/> Cadastrar proteína</Button> : null}
          />
        ) : (
          <div className="divide-y divide-slate-200">
            {proteins.map((p) => {
              const preps = prepsByProtein[p.id] || [];
              const open = expanded[p.id];
              return (
                <div key={p.id} data-testid={`protein-row-${p.id}`}>
                  <div className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50">
                    <button onClick={() => setExpanded({ ...expanded, [p.id]: !open })} className="text-slate-500" data-testid={`toggle-protein-${p.id}`}>
                      {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    <div className="flex-1">
                      <div className="font-medium text-slate-900">{p.name}</div>
                      <div className="text-xs text-slate-500 capitalize">
                        {PROTEIN_CATEGORIES.find(c => c.value === p.category)?.label || p.category} · {preps.length} preparação(ões)
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => openCreatePrep(p.id)} data-testid={`add-prep-for-${p.id}`}>
                      <Plus size={14} className="mr-1" /> Preparação
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditProtein(p)} data-testid={`edit-protein-${p.id}`}><Pencil size={15} /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => setDeleteProteinId(p.id)} data-testid={`delete-protein-${p.id}`}><Trash2 size={15} /></Button>
                  </div>
                  {open && (
                    <div className="bg-slate-50/50 border-t border-slate-100">
                      {preps.length === 0 ? (
                        <div className="px-6 py-4 text-sm text-slate-500">Nenhuma preparação. {canWritePrep && <button className="text-emerald-800 hover:underline" onClick={() => openCreatePrep(p.id)}>Cadastrar agora</button>}</div>
                      ) : (
                        <table className="mp-table w-full">
                          <thead>
                            <tr>
                              <th className="pl-10">Preparação</th>
                              <th className="mp-num">Gramas/refeição</th>
                              <th>Observações</th>
                              {canWritePrep && <th className="text-right">Ações</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {preps.map((pr) => (
                              <tr key={pr.id} data-testid={`prep-row-${pr.id}`}>
                                <td className="pl-10">{pr.name}</td>
                                <td className="mp-num tabular-nums">{pr.grams_per_meal} g</td>
                                <td className="text-slate-600">{pr.notes || "—"}</td>
                                {canWritePrep && (
                                  <td>
                                    <div className="flex items-center justify-end gap-1">
                                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditPrep(pr)} data-testid={`edit-prep-${pr.id}`}><Pencil size={14} /></Button>
                                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" onClick={() => setDeletePrepId(pr.id)} data-testid={`delete-prep-${pr.id}`}><Trash2 size={14} /></Button>
                                    </div>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Protein Dialog */}
      <Dialog open={pOpen} onOpenChange={setPOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>{pEditId ? "Editar proteína" : "Nova proteína"}</DialogTitle></DialogHeader>
          <form onSubmit={submitProtein} className="space-y-4">
            <div>
              <Label>Nome *</Label>
              <Input required value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} placeholder="Ex.: Frango, Contra-filé, Tilápia" data-testid="protein-name-input" />
            </div>
            <div>
              <Label>Categoria</Label>
              <Select value={pForm.category} onValueChange={(v) => setPForm({ ...pForm, category: v })}>
                <SelectTrigger data-testid="protein-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROTEIN_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Observações</Label>
              <Textarea value={pForm.notes} onChange={(e) => setPForm({ ...pForm, notes: e.target.value })} data-testid="protein-notes-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="protein-submit-btn" className="bg-emerald-900 hover:bg-emerald-800 text-white">{pEditId ? "Salvar" : "Cadastrar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preparation Dialog */}
      <Dialog open={prepOpen} onOpenChange={setPrepOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>{prepEditId ? "Editar preparação" : "Nova preparação"}</DialogTitle></DialogHeader>
          <form onSubmit={submitPrep} className="space-y-4">
            <div>
              <Label>Proteína *</Label>
              <Select value={prepForm.protein_id} onValueChange={(v) => setPrepForm({ ...prepForm, protein_id: v })}>
                <SelectTrigger data-testid="prep-protein-select"><SelectValue placeholder="Selecione a proteína" /></SelectTrigger>
                <SelectContent>
                  {proteins.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nome da preparação *</Label>
              <Input required value={prepForm.name} onChange={(e) => setPrepForm({ ...prepForm, name: e.target.value })} placeholder="Ex.: Frango grelhado, Frango à parmegiana" data-testid="prep-name-input" />
            </div>
            <div>
              <Label>Gramas por refeição *</Label>
              <Input type="number" min={1} step="1" required value={prepForm.grams_per_meal} onChange={(e) => setPrepForm({ ...prepForm, grams_per_meal: e.target.value })} data-testid="prep-grams-input" />
            </div>
            <div>
              <Label>Observações</Label>
              <Textarea value={prepForm.notes} onChange={(e) => setPrepForm({ ...prepForm, notes: e.target.value })} data-testid="prep-notes-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPrepOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="prep-submit-btn" className="bg-emerald-900 hover:bg-emerald-800 text-white">{prepEditId ? "Salvar" : "Cadastrar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteProteinId} onOpenChange={(o) => !o && setDeleteProteinId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader><AlertDialogTitle>Remover proteína?</AlertDialogTitle>
            <AlertDialogDescription>Todas as preparações desta proteína também serão removidas.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDeleteProtein} data-testid="confirm-delete-protein" className="bg-red-600 hover:bg-red-700 text-white">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deletePrepId} onOpenChange={(o) => !o && setDeletePrepId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader><AlertDialogTitle>Remover preparação?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDeletePrep} data-testid="confirm-delete-prep" className="bg-red-600 hover:bg-red-700 text-white">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
